import React from 'react';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Box,
} from '@mui/material';
import {useSubscription} from 'context/SubscriptionContext';
import {getEntitiesForContext} from 'utils/entitySelectorUtils';
import {
  SubscriptionTypes,
  SubscriptionTypeLabels,
  SubscriptionTypeDescriptions,
} from 'constants/subscriptionTypes';
import DurationDropdown from 'components/Duration/DurationDropdown';
import CountryDropdown from 'components/country/CountryDropdown';

// All four "which subscription is this" fields in one compact block.
const SubscriptionSetupSection = ({
  isEditMode,
  errors = {},
  onTypeChange,
  onModuleChange,
  onDurationChange,
  onCountryChange,
}) => {
  const {subscriptionType, moduleType, duration, country} = useSubscription();
  const subscriptionEntities = getEntitiesForContext('SUBSCRIPTION');

  const lockedHelper = 'Cannot be changed while editing';

  return (
    <Grid
      container
      spacing={2.5}
      sx={{
        // Duration/Country dropdowns render larger text than the MUI selects
        '& .MuiOutlinedInput-root, & .MuiOutlinedInput-root .MuiSelect-select': {
          fontSize: '0.9375rem',
        },
      }}>
      <Grid item xs={12} sm={6}>
        <FormControl
          fullWidth
          required
          error={!!errors.subscriptionType}
          disabled={isEditMode}>
          <InputLabel id="subscription-type-label">Subscription Type</InputLabel>
          <Select
            labelId="subscription-type-label"
            label="Subscription Type"
            value={subscriptionType || ''}
            onChange={e => onTypeChange(e.target.value)}
            renderValue={value => SubscriptionTypeLabels[value]}
            MenuProps={{PaperProps: {sx: {maxWidth: 440}}}}>
            {Object.values(SubscriptionTypes).map(type => (
              <MenuItem key={type} value={type} sx={{whiteSpace: 'normal'}}>
                <Box>
                  <Typography variant="body2" sx={{fontWeight: 600}}>
                    {SubscriptionTypeLabels[type]}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {SubscriptionTypeDescriptions[type]}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {errors.subscriptionType ||
              (isEditMode
                ? lockedHelper
                : subscriptionType
                ? SubscriptionTypeDescriptions[subscriptionType]
                : 'How clients will pay')}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl
          fullWidth
          required
          error={!!errors.moduleType}
          disabled={isEditMode}>
          <InputLabel id="module-type-label">Module</InputLabel>
          <Select
            labelId="module-type-label"
            label="Module"
            value={moduleType || ''}
            onChange={e => onModuleChange(e.target.value)}
            renderValue={value => subscriptionEntities[value]?.label || value}>
            {Object.entries(subscriptionEntities).map(([key, entity]) => (
              <MenuItem key={key} value={key}>
                <Box>
                  <Typography variant="body2" sx={{fontWeight: 600}}>
                    {entity.label}
                  </Typography>
                  {entity.description && (
                    <Typography variant="caption" color="textSecondary">
                      {entity.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {errors.moduleType ||
              (isEditMode ? lockedHelper : 'What this configuration applies to')}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <DurationDropdown
          value={duration}
          onChange={onDurationChange}
          label="Duration"
          required
          showActiveOnly
          showDefaultFirst
          fullWidth
          error={errors.duration}
          disabled={isEditMode}
          helperText={isEditMode ? lockedHelper : 'How long the subscription lasts'}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <CountryDropdown
          value={country}
          onChange={onCountryChange}
          label="Country"
          required
          showActiveOnly
          fullWidth
          showSearch
          includeFlag
          error={errors.country}
          disabled={isEditMode}
          helperText={isEditMode ? lockedHelper : 'Region this applies to'}
        />
      </Grid>
    </Grid>
  );
};

export default SubscriptionSetupSection;
